/**
 * RAAW utilities — countdown, shop-the-look, size guide dialog.
 * No external frameworks. Safe for Theme Editor re-init.
 */
(() => {
  const INIT = 'data-raaw-init';
  const SIZE_GUIDE_INIT = 'data-raaw-size-guide-init';

  function initCountdown(root) {
    const target = root.getAttribute('data-target');
    if (!target) return;

    const end = Date.parse(target);
    if (Number.isNaN(end)) {
      root.hidden = true;
      return;
    }

    const endedMode = root.getAttribute('data-ended') || 'hide';
    const endedLabel = root.getAttribute('data-ended-label') || '';
    const units = {
      days: root.querySelector('[data-unit="days"]'),
      hours: root.querySelector('[data-unit="hours"]'),
      minutes: root.querySelector('[data-unit="minutes"]'),
      seconds: root.querySelector('[data-unit="seconds"]'),
    };

    const tick = () => {
      const diff = end - Date.now();
      if (diff <= 0) {
        if (endedMode === 'message' && endedLabel) {
          const p = document.createElement('p');
          p.className = 'raaw-drop__released';
          p.textContent = endedLabel;
          root.replaceChildren(p);
        } else {
          root.hidden = true;
        }
        return false;
      }

      const totalSec = Math.floor(diff / 1000);
      const days = Math.floor(totalSec / 86400);
      const hours = Math.floor((totalSec % 86400) / 3600);
      const minutes = Math.floor((totalSec % 3600) / 60);
      const seconds = totalSec % 60;

      if (units.days) units.days.textContent = String(days).padStart(2, '0');
      if (units.hours) units.hours.textContent = String(hours).padStart(2, '0');
      if (units.minutes) units.minutes.textContent = String(minutes).padStart(2, '0');
      if (units.seconds) units.seconds.textContent = String(seconds).padStart(2, '0');
      return true;
    };

    if (!tick()) return;
    const id = window.setInterval(() => {
      if (!tick()) window.clearInterval(id);
    }, 1000);
    root._raawCountdownId = id;
  }

  function initShopTheLook(root) {
    const buttons = root.querySelectorAll('[data-raaw-hotspot]');
    const panels = root.querySelectorAll('[data-raaw-hotspot-panel]');
    const controller = new AbortController();
    root._raawAbort = controller;

    const closeAll = () => {
      buttons.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      panels.forEach((panel) => {
        panel.hidden = true;
        panel.removeAttribute('open');
      });
    };

    buttons.forEach((btn) => {
      btn.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          const id = btn.getAttribute('aria-controls');
          const panel = id ? root.querySelector(`#${CSS.escape(id)}`) : null;
          const open = btn.getAttribute('aria-expanded') === 'true';
          closeAll();
          if (!open && panel) {
            btn.setAttribute('aria-expanded', 'true');
            panel.hidden = false;
            panel.setAttribute('open', '');
            const x = Number(btn.style.getPropertyValue('--hx') || 50);
            const y = Number(btn.style.getPropertyValue('--hy') || 50);
            panel.style.left = `${Math.min(Math.max(x, 8), 72)}%`;
            panel.style.top = `${Math.min(Math.max(y + 4, 8), 78)}%`;
          }
        },
        { signal: controller.signal }
      );
    });

    root.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') closeAll();
      },
      { signal: controller.signal }
    );

    document.addEventListener(
      'click',
      (event) => {
        if (!root.contains(event.target)) closeAll();
      },
      { signal: controller.signal }
    );
  }

  function initSizeGuide(root) {
    if (root.getAttribute(SIZE_GUIDE_INIT) === 'true') return;
    root.setAttribute(SIZE_GUIDE_INIT, 'true');

    const openBtn = root.querySelector('[data-raaw-size-guide-open]');
    const dialog = root.querySelector('[data-raaw-size-guide-dialog]');
    if (!openBtn || !dialog || typeof dialog.showModal !== 'function') return;

    const closeBtns = root.querySelectorAll('[data-raaw-size-guide-close]');
    let lastFocus = null;

    const close = () => {
      if (dialog.open) dialog.close();
    };

    openBtn.addEventListener('click', () => {
      if (openBtn.disabled) return;
      lastFocus = document.activeElement;
      dialog.showModal();
    });

    closeBtns.forEach((btn) => btn.addEventListener('click', close));

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });

    dialog.addEventListener('close', () => {
      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      }
      lastFocus = null;
    });
  }

  function boot(scope = document) {
    scope.querySelectorAll(`[data-raaw-countdown]:not([${INIT}])`).forEach((el) => {
      el.setAttribute(INIT, 'true');
      initCountdown(el);
    });

    scope.querySelectorAll(`[data-raaw-stl]:not([${INIT}])`).forEach((el) => {
      el.setAttribute(INIT, 'true');
      initShopTheLook(el);
    });

    scope.querySelectorAll('.raaw-size-guide').forEach((el) => initSizeGuide(el));
  }

  function teardown(scope) {
    scope.querySelectorAll(`[${INIT}]`).forEach((el) => {
      if (el._raawCountdownId) window.clearInterval(el._raawCountdownId);
      if (el._raawAbort) el._raawAbort.abort();
      el.removeAttribute(INIT);
    });
    scope.querySelectorAll(`[${SIZE_GUIDE_INIT}]`).forEach((el) => el.removeAttribute(SIZE_GUIDE_INIT));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));
})();
